import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useAuth } from 'providers';
import { useCookies } from 'react-cookie';
import { Typography } from '@mui/material';
import { useAddToHomeScreen } from 'hooks';
import usePageTitle from 'hooks/usePageTitle';
import getUserDevice from 'utils/getUserDevice';
import IOSShareIcon from 'assets/icons/IOSShareIcon';
import CookieConsent from 'views/modals/CookieConsent';
import FoodGraphics from 'assets/images/graphic-login.png';
import AddToHomeScreenIcon from 'assets/icons/AddToHomeScreenIcon';
import ZillionaireLogo from 'assets/images/graphic-zillionaires-club.svg';
import { pushAnalyticsEvent } from 'helpers/analytics';
import {
  Text,
  Logo,
  Content,
  Graphic,
  Wrapper,
  Welcome,
  StyledText,
  WelcomeText,
  StyledPaper,
  TextWrapper,
  IconWrapper,
  StyledLayout,
  StyledButton,
  AndroidButton,
  AddToHomeScreenWrapper,
} from './Login.styles';

const Login = () => {
  usePageTitle('Welcome');
  const userDevice = getUserDevice();
  const isRunningAsApp =
    navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;

  useEffect(() => {
    if (isRunningAsApp) {
      pushAnalyticsEvent({
        event: 'accessLocation',
        location: 'Home Screen',
        operationSystem: userDevice,
      });
    } else {
      pushAnalyticsEvent({
        event: 'accessLocation',
        location: 'Browser',
        operationSystem: userDevice,
      });
    }
  }, [isRunningAsApp, userDevice]);

  const { signIn: handleOnSignIn } = useAuth();
  const { addToHomeScreen, showPrompt } = useAddToHomeScreen();
  const [cookies, setCookies] = useCookies<string>(['hasConsented']);

  const [consentRequired, setConsentRequired] = useState(true);

  const { hasConsented, CookieControl } = cookies;
  const { optionalCookies } = CookieControl || {};

  let hasZizziConsent = false;

  if (CookieControl && optionalCookies) {
    const { analytics, marketing } = optionalCookies;
    hasZizziConsent = [analytics, marketing].every((value) => value === 'accepted');
  }

  const hasGivenConsent = consentRequired && (hasConsented || hasZizziConsent);

  if (hasGivenConsent) setConsentRequired(false);

  const handleOnCookieConsent = () => {
    setCookies('hasConsented', true, {
      expires: new Date(dayjs().add(1, 'year').toString()),
    });
    setConsentRequired(false);
  };

  const handleOnAddToHomeScreen = () => {
    addToHomeScreen('Landing Page');
  };

  const Header = () => (
    <Welcome>
      <Content>
        <WelcomeText variant="h2">Welcome to</WelcomeText>
        <Logo src={ZillionaireLogo} />
      </Content>
    </Welcome>
  );

  const SignInCard = () => (
    <StyledPaper>
      <Text variant="h3">Home of Zizzi perkz (free food) and exclusive discounts</Text>
      <Graphic src={FoodGraphics} />
      <StyledButton onClick={handleOnSignIn} fullWidth variant="contained">
        Let&apos;s go!
      </StyledButton>
    </StyledPaper>
  );

  const AddToHomeScreen = () => (
    <AddToHomeScreenWrapper>
      <IconWrapper>
        <AddToHomeScreenIcon fill="#fef9ec" />
      </IconWrapper>
      <TextWrapper>
        <StyledText variant="h4">
          Access your perkz more easily by adding the club to your home screen
        </StyledText>
        {userDevice === 'Android' ? (
          <AndroidButton onClick={handleOnAddToHomeScreen}>Yes, add it now! </AndroidButton>
        ) : (
          <Typography>
            Simply tap <IOSShareIcon /> then &apos;Add to Home screen&apos;
          </Typography>
        )}
      </TextWrapper>
    </AddToHomeScreenWrapper>
  );

  return (
    <StyledLayout hideHeader>
      <Wrapper aria-label="Login">
        <CookieConsent isOpen={consentRequired} onClose={handleOnCookieConsent} />
        <Header />
        <SignInCard />
        {showPrompt && <AddToHomeScreen />}
      </Wrapper>
    </StyledLayout>
  );
};

export default Login;
